import {
  ArrowDownTrayIcon,
  MicrophoneIcon,
  PaintBrushIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import {
  useAdForgeCreateOverlayLayoutMutation,
  useAdForgeDeleteOverlayLayoutMutation,
  useAdForgeGetBrandKitQuery,
  useAdForgeListOverlayLayoutsQuery,
  useAdForgeOverlayCanvasMutation,
  useAdForgeProxyImageMutation,
  useAdForgeProxyVideoMutation,
  useAdForgeUpdateOverlayLayoutMutation,
  type OverlayCanvasTurn,
  type OverlayDoc,
  type OverlayLayout,
} from "~/ui-rtk/api/adForgeApi"
import { useGetAssetQuery } from "~/ui-rtk/api/assetsApi"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import type { ScrapedImage } from "../../types"
import type { AiUploadOptions } from "../AiActionDialog"
import BrandKitModal from "./BrandKitModal"
import {
  downloadBlob,
  exportImageWithOverlay,
  exportVideoWithOverlay,
  loadImage,
} from "./export-overlay"
import OverlayCanvasEditor from "./OverlayCanvasEditor"
import { useCustomBrandFonts } from "./useCustomBrandFonts"
import { useSpeechToText } from "./useSpeechToText"
import {
  ensureFontsLoaded,
  fontFamiliesInDoc,
  renderOverlay,
} from "./render-overlay"

/**
 * Overlay layouts — the layout-first "Brand overlay" flow:
 *   1. BUILD a layout on a bare canvas (checkerboard, pick an aspect ratio):
 *      AI designs/edits the document, you fine-tune by hand, save it as a
 *      named, reusable layout.
 *   2. While creating an ad, pick media (✨ on a card or the Apply-to strip)
 *      and SELECT a saved layout — it rescales onto the media, stays fully
 *      editable, then bakes client-side (Save to assets / Download).
 * Both halves edit the same OverlayDoc the AI edits via
 * /ad-forge/overlay-canvas, constrained by the Brand Kit.
 */

const PRESETS: Array<{ key: string; label: string }> = [
  { key: "announcement", label: "Announcement" },
  { key: "promo", label: "Promo / sale" },
  { key: "cta", label: "Call to action" },
  { key: "product", label: "Product highlight" },
  { key: "minimal", label: "Minimal branding" },
]

// Longest canvas edge. Exports render at canvas size, so this bounds export
// cost; the doc scales with the media's true aspect either way.
const MAX_CANVAS_EDGE = 2048

/**
 * Canvas ratio options — Meta placement sizes. "original" tracks the media's
 * natural dimensions (only available once media is picked); the rest force
 * the canvas and cover-crop the media (editor `object-cover` = export
 * `drawCover`).
 */
const RATIOS: Array<{
  key: string
  label: string
  width?: number
  height?: number
}> = [
  { key: "original", label: "Original" },
  { key: "1:1", label: "1:1", width: 1080, height: 1080 },
  { key: "4:5", label: "4:5", width: 1080, height: 1350 },
  { key: "9:16", label: "9:16", width: 1080, height: 1920 },
  { key: "16:9", label: "16:9", width: 1920, height: 1080 },
  { key: "1.91:1", label: "1.91:1", width: 1200, height: 628 },
]

const DEFAULT_BUILDER_RATIO = "4:5"

const dimsForRatio = (key: string): { width: number; height: number } => {
  const ratio = RATIOS.find(r => r.key === key)
  return ratio?.width && ratio?.height
    ? { width: ratio.width, height: ratio.height }
    : { width: 1080, height: 1350 }
}

/**
 * Fit an existing document into new canvas dimensions. The flow layout
 * re-solves positions on its own — only SIZES need scaling: type, gaps and
 * insets scale by the per-axis factors (smaller factor for type so nothing
 * blows out).
 */
export function rescaleDoc(
  doc: OverlayDoc,
  canvas: { width: number; height: number },
): OverlayDoc {
  if (
    doc.canvas.width === canvas.width &&
    doc.canvas.height === canvas.height
  ) {
    return { ...doc, canvas }
  }
  const sx = canvas.width / doc.canvas.width
  const sy = canvas.height / doc.canvas.height
  const s = Math.min(sx, sy)
  return {
    ...doc,
    canvas,
    sections: doc.sections.map(section => ({
      ...section,
      gap: Math.round(section.gap * s),
      paddingX: Math.round(section.paddingX * sx),
      paddingY: Math.round(section.paddingY * sy),
      items: section.items.map(item => {
        // Free-placed `position` is percentages — ratio-independent, carries
        // over untouched. Only px SIZES scale.
        if (item.type === "text") {
          return {
            ...item,
            fontSize: Math.max(8, Math.round(item.fontSize * s)),
            ...(item.letterSpacing
              ? { letterSpacing: item.letterSpacing * s }
              : {}),
            ...(item.background
              ? {
                  background: {
                    ...item.background,
                    paddingX: Math.round(item.background.paddingX * s),
                    paddingY: Math.round(item.background.paddingY * s),
                    radius: Math.round(item.background.radius * s),
                  },
                }
              : {}),
          }
        }
        if (item.type === "logo" || item.type === "divider") {
          return {
            ...item,
            width: Math.max(2, Math.round(item.width * s)),
            height: Math.max(1, Math.round(item.height * s)),
          }
        }
        return {
          ...item,
          size: Math.max(1, Math.round(item.size * s)),
        }
      }),
    })),
  }
}

/** Mini canvas preview of a layout doc (neutral backdrop, no logo image). */
export function LayoutThumb({ doc }: { doc: OverlayDoc }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    let cancelled = false
    const draw = (): void => {
      const canvas = canvasRef.current
      if (cancelled || !canvas) return
      const width = 120
      const height = Math.max(
        24,
        Math.round((doc.canvas.height / doc.canvas.width) * width),
      )
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext("2d")
      if (!ctx) return
      ctx.fillStyle = "#242938"
      ctx.fillRect(0, 0, width, height)
      renderOverlay(ctx, doc, width / doc.canvas.width)
    }
    // First paint immediately (fallback fonts), repaint once brand fonts land.
    draw()
    void ensureFontsLoaded(fontFamiliesInDoc(doc)).then(draw)
    return () => {
      cancelled = true
    }
  }, [doc])
  return <canvas ref={canvasRef} className="h-20 w-auto rounded" />
}

interface BrandOverlayPanelProps {
  /** Media to apply a layout to — null opens the layout builder/library. */
  asset: ScrapedImage | null
  /** Staged + pool assets offered as apply targets while no media is set. */
  candidates?: ScrapedImage[]
  onSelectAsset?: (asset: ScrapedImage) => void
  /** Clears the current media (back to builder mode, doc kept). */
  onClearAsset?: () => void
  onGenerated: (
    file: File,
    uploadOptions?: AiUploadOptions,
  ) => Promise<void> | void
  onClose: () => void
}

export default function BrandOverlayPanel({
  asset,
  candidates,
  onSelectAsset,
  onClearAsset,
  onGenerated,
  onClose,
}: BrandOverlayPanelProps) {
  const isVideo = asset?.type === "video"

  // Register uploaded brand fonts for previews/exports (module-deduped).
  useCustomBrandFonts()

  const { data: brandKit, isLoading: brandKitLoading } =
    useAdForgeGetBrandKitQuery()
  const { data: logoAsset } = useGetAssetQuery(brandKit?.logoAssetId ?? "", {
    skip: !brandKit?.logoAssetId,
  })
  const logoUrl = logoAsset?.url || logoAsset?.thumbnailUrl || null

  const { data: layouts, isLoading: layoutsLoading } =
    useAdForgeListOverlayLayoutsQuery()
  const [createLayout, { isLoading: isCreatingLayout }] =
    useAdForgeCreateOverlayLayoutMutation()
  const [updateLayout, { isLoading: isUpdatingLayout }] =
    useAdForgeUpdateOverlayLayoutMutation()
  const [deleteLayout] = useAdForgeDeleteOverlayLayoutMutation()

  const [generateOverlay, { isLoading: aiBusy }] =
    useAdForgeOverlayCanvasMutation()
  const [proxyImage] = useAdForgeProxyImageMutation()
  const [proxyVideo] = useAdForgeProxyVideoMutation()

  const [canvasDims, setCanvasDims] = useState<{
    width: number
    height: number
  } | null>(asset ? null : dimsForRatio(DEFAULT_BUILDER_RATIO))
  const [ratioKey, setRatioKey] = useState(
    asset ? "original" : DEFAULT_BUILDER_RATIO,
  )
  // Media's natural (capped) dims — what "Original" restores.
  const naturalDimsRef = useRef<{ width: number; height: number } | null>(null)

  const [doc, setDoc] = useState<OverlayDoc | null>(null)
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null)
  const [instruction, setInstruction] = useState("")
  const [preset, setPreset] = useState<string | null>(null)
  // The iteration thread with the AI — sent (recent tail) on every round-trip
  // so "undo that" / "same but bolder" resolve against prior turns.
  const [conversation, setConversation] = useState<OverlayCanvasTurn[]>([])
  // Doc snapshots taken before each applied AI result — Undo for AI turns.
  const [undoStack, setUndoStack] = useState<OverlayDoc[]>([])
  const [activeLayoutId, setActiveLayoutId] = useState<string | null>(null)
  const [layoutName, setLayoutName] = useState("")
  const [brandKitOpen, setBrandKitOpen] = useState(false)
  const [exporting, setExporting] = useState<{
    kind: "save" | "download"
    progress: number | null
  } | null>(null)

  // Taint-free source cache — proxied once per asset, reused across exports.
  const cleanSrcRef = useRef<{ assetId?: string; src: string } | null>(null)

  // Dictate the AI instruction (browser SpeechRecognition, English) —
  // finalized phrases append to whatever's already typed.
  const speech = useSpeechToText(text =>
    setInstruction(prev => (prev ? `${prev.trimEnd()} ${text}` : text)),
  )

  // Measure media whenever it changes. The document SURVIVES media changes —
  // that's the whole point (design once, apply to any media): it just
  // rescales into the new canvas space.
  useEffect(() => {
    cleanSrcRef.current = null
    if (!asset) {
      naturalDimsRef.current = null
      return
    }
    let cancelled = false

    const applyDims = (width: number, height: number): void => {
      if (cancelled) return
      const w = width || 1080
      const h = height || 1080
      const scale = Math.min(1, MAX_CANVAS_EDGE / Math.max(w, h))
      const dims = {
        width: Math.round(w * scale),
        height: Math.round(h * scale),
      }
      naturalDimsRef.current = dims
      setRatioKey("original")
      setCanvasDims(dims)
      setDoc(prev => (prev ? rescaleDoc(prev, dims) : prev))
    }

    if (asset.width && asset.height) {
      applyDims(asset.width, asset.height)
    } else if (asset.type === "video") {
      const video = document.createElement("video")
      video.onloadedmetadata = () =>
        applyDims(video.videoWidth, video.videoHeight)
      video.onerror = () => applyDims(1080, 1080)
      video.src = asset.url
    } else {
      loadImage(asset.url).then(
        img => applyDims(img.naturalWidth, img.naturalHeight),
        () => applyDims(1080, 1080),
      )
    }
    return () => {
      cancelled = true
    }
  }, [asset?.id])

  const changeRatio = (key: string): void => {
    const dims = key === "original" ? naturalDimsRef.current : dimsForRatio(key)
    if (!dims) return
    setRatioKey(key)
    setCanvasDims(dims)
    setDoc(prev => (prev ? rescaleDoc(prev, dims) : prev))
  }

  const runAi = async (): Promise<void> => {
    if (!canvasDims) return
    speech.stop()
    const trimmed = instruction.trim()
    if (!trimmed && !preset) {
      toast.warning("Describe the overlay or pick a preset first")
      return
    }
    const userText = trimmed || `Design a ${preset} overlay layout.`
    try {
      const result = await generateOverlay({
        assetId: asset?.assetId,
        canvasWidth: canvasDims.width,
        canvasHeight: canvasDims.height,
        currentDoc: doc ?? undefined,
        instruction: userText,
        preset: preset ?? undefined,
        history: conversation.slice(-10),
        // Selected element = the instruction's target; the AI leaves the
        // rest of the document untouched.
        targetId: doc && selectedLayerId ? selectedLayerId : undefined,
      }).unwrap()
      if (doc) setUndoStack(prev => [...prev.slice(-9), doc])
      setDoc(result.doc)
      setConversation(prev =>
        [
          ...prev,
          // Truncate defensively — the history DTO caps each turn at 2000
          // chars (same as the instruction cap).
          { role: "user" as const, text: userText.slice(0, 2000) },
          {
            role: "ai" as const,
            text: (result.notes?.trim() || "Updated the canvas.").slice(
              0,
              2000,
            ),
          },
        ].slice(-24),
      )
      setSelectedLayerId(null)
      setInstruction("")
      if (result.doc.sections.length === 0) {
        toast.warning(
          "The AI returned an empty canvas — try a more specific instruction",
        )
      }
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        "Overlay generation failed"
      toast.error(message)
    }
  }

  const undoAiTurn = (): void => {
    const previous = undoStack[undoStack.length - 1]
    if (!previous) return
    setUndoStack(prev => prev.slice(0, -1))
    setDoc(canvasDims ? rescaleDoc(previous, canvasDims) : previous)
    setSelectedLayerId(null)
    // Tell the AI on the next turn that its last change was rolled back.
    setConversation(prev =>
      [
        ...prev,
        { role: "user" as const, text: "(manually reverted your last change)" },
      ].slice(-24),
    )
  }

  const startBlank = (): void => {
    if (!canvasDims) return
    setDoc({ version: 2, canvas: canvasDims, sections: [] })
    setActiveLayoutId(null)
    setLayoutName("")
  }

  const startNewLayout = (): void => {
    setDoc(null)
    setActiveLayoutId(null)
    setLayoutName("")
    setSelectedLayerId(null)
    setConversation([])
    setUndoStack([])
  }

  const openLayout = (layout: OverlayLayout): void => {
    const dims = canvasDims ?? layout.doc.canvas
    if (!canvasDims) setCanvasDims(dims)
    setDoc(rescaleDoc(layout.doc, dims))
    setActiveLayoutId(layout.id)
    setLayoutName(layout.name)
    setSelectedLayerId(null)
    setConversation([])
    setUndoStack([])
  }

  const ratioLabel =
    ratioKey === "original" && canvasDims
      ? `${canvasDims.width}:${canvasDims.height}`
      : ratioKey

  const handleSaveLayout = async (): Promise<void> => {
    if (!doc || doc.sections.length === 0) {
      toast.warning("The canvas is empty — nothing to save as a layout")
      return
    }
    const name = layoutName.trim()
    if (!name) {
      toast.warning("Give the layout a name first")
      return
    }
    try {
      if (activeLayoutId) {
        await updateLayout({
          id: activeLayoutId,
          body: { name, aspectRatio: ratioLabel, doc },
        }).unwrap()
      } else {
        const created = await createLayout({
          name,
          aspectRatio: ratioLabel,
          doc,
        }).unwrap()
        setActiveLayoutId(created.id)
      }
      toast.success(`Layout "${name}" saved`)
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        "Failed to save layout"
      toast.error(message)
    }
  }

  const handleDeleteLayout = async (layout: OverlayLayout): Promise<void> => {
    try {
      await deleteLayout(layout.id).unwrap()
      if (activeLayoutId === layout.id) setActiveLayoutId(null)
      toast.success(`Layout "${layout.name}" deleted`)
    } catch {
      toast.error("Failed to delete layout")
    }
  }

  const getCleanBaseSrc = async (): Promise<string> => {
    if (!asset) throw new Error("Pick media to apply the layout to first")
    const cached = cleanSrcRef.current
    if (cached && cached.assetId === asset.assetId) return cached.src
    const src = isVideo
      ? (await proxyVideo({ videoUri: asset.url }).unwrap()).videoUrl
      : (await proxyImage({ url: asset.url }).unwrap()).base64
    cleanSrcRef.current = { assetId: asset.assetId, src }
    return src
  }

  const getLogoForExport = async (): Promise<HTMLImageElement | null> => {
    const hasLogoItem = doc?.sections.some(section =>
      section.items.some(item => item.type === "logo"),
    )
    if (!hasLogoItem || !logoUrl) {
      return null
    }
    const { base64 } = await proxyImage({ url: logoUrl }).unwrap()
    return loadImage(base64)
  }

  const handleExport = async (kind: "save" | "download"): Promise<void> => {
    if (!doc || !asset) return
    if (doc.sections.length === 0) {
      toast.warning("The canvas is empty — nothing to export")
      return
    }
    setExporting({ kind, progress: null })
    try {
      const [baseSrc, logo] = await Promise.all([
        getCleanBaseSrc(),
        getLogoForExport(),
      ])
      const file = isVideo
        ? await exportVideoWithOverlay({
            videoSrc: baseSrc,
            doc,
            assets: { logo },
            onProgress: fraction =>
              setExporting(prev =>
                prev ? { ...prev, progress: fraction } : prev,
              ),
          })
        : await exportImageWithOverlay({ baseSrc, doc, assets: { logo } })

      if (kind === "download") {
        downloadBlob(file, file.name)
        toast.success("Overlay downloaded")
      } else {
        await onGenerated(file, {
          sourceType: "GEN_AI",
          parentAssetId: asset.assetId,
          metadata: {
            generatedBy: "ai_brand_overlay",
            aspectRatio: `${doc.canvas.width}:${doc.canvas.height}`,
            // The document travels with the asset so the overlay stays
            // editable later; the layout id records provenance.
            overlayDoc: doc,
            ...(activeLayoutId ? { overlayLayoutId: activeLayoutId } : {}),
          },
        })
        toast.success("Overlay saved to your assets")
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Export failed"
      toast.error(message)
    } finally {
      setExporting(null)
    }
  }

  const isSavingLayout = isCreatingLayout || isUpdatingLayout
  const busy = aiBusy || exporting !== null || isSavingLayout

  // Human-readable label for the selected element (the AI's target).
  const selectionLabel = ((): string | null => {
    if (!doc || !selectedLayerId) return null
    for (const section of doc.sections) {
      if (section.id === selectedLayerId) return `${section.anchor} section`
      for (const item of section.items) {
        if (item.id !== selectedLayerId) continue
        if (item.type === "text") {
          return `"${item.text.slice(0, 28)}${item.text.length > 28 ? "…" : ""}"`
        }
        return item.type
      }
    }
    return null
  })()

  return (
    <div className="flex flex-col gap-3">
      {/* Saved layouts */}
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wide text-system-gray-500 pt-1">
          Layouts
        </span>
        {layoutsLoading ? (
          <span className="inline-flex items-center gap-1.5 text-[10px] text-system-gray-400 pt-1">
            <span className="loading loading-spinner loading-xs" />
            Loading layouts…
          </span>
        ) : (
          <>
            {(layouts ?? []).map(layout => (
              <div
                key={layout.id}
                className={cn(
                  "relative group rounded-lg border p-1 cursor-pointer",
                  activeLayoutId === layout.id
                    ? "border-brand/70 bg-brand/10"
                    : "border-system-gray-700 hover:border-brand/40",
                )}
                onClick={() => openLayout(layout)}
                title={`${layout.name} (${layout.aspectRatio})`}
              >
                <LayoutThumb doc={layout.doc} />
                <span className="block max-w-[7.5rem] truncate text-[10px] text-system-gray-300 text-center">
                  {layout.name}
                </span>
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation()
                    void handleDeleteLayout(layout)
                  }}
                  className="absolute -top-1.5 -right-1.5 hidden group-hover:flex items-center justify-center w-5 h-5 rounded-full bg-system-gray-800 border border-system-gray-600 text-system-gray-400 hover:text-system-red-400"
                  aria-label={`Delete layout ${layout.name}`}
                  title="Delete layout"
                >
                  <TrashIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={startNewLayout}
              className="flex flex-col items-center justify-center gap-1 h-[6.5rem] w-20 rounded-lg border border-dashed border-system-gray-700 text-system-gray-400 hover:text-white hover:border-brand/50"
            >
              <PlusIcon className="w-4 h-4" />
              <span className="text-[10px]">New layout</span>
            </button>
          </>
        )}
      </div>

      {/* Brand kit summary + apply target */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setBrandKitOpen(true)}
          className="btn btn-xs bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white gap-1.5"
        >
          <PaintBrushIcon className="w-3.5 h-3.5" />
          Brand Kit
        </button>
        {brandKitLoading ? (
          <span className="inline-flex items-center gap-1.5 text-[10px] text-system-gray-400">
            <span className="loading loading-spinner loading-xs" />
            Loading brand kit…
          </span>
        ) : (
          !brandKit?.colors?.primary && (
            <span className="text-[10px] text-system-yellow-400">
              No brand kit yet — set colors, logo and rules so the AI designs
              on-brand.
            </span>
          )
        )}

        <span className="ml-auto" />
        {asset ? (
          <span className="inline-flex items-center gap-1.5 text-[10px] text-system-gray-400">
            Applying to
            <img
              src={asset.thumbnailUrl || asset.url}
              alt=""
              className="w-6 h-6 rounded object-cover"
            />
            {onClearAsset && (
              <button
                type="button"
                onClick={onClearAsset}
                className="p-0.5 rounded hover:bg-system-gray-800 text-system-gray-500 hover:text-white"
                aria-label="Clear media"
                title="Back to layout builder"
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            )}
          </span>
        ) : (
          (candidates?.length ?? 0) > 0 && (
            <span className="inline-flex items-center gap-1.5 text-[10px] text-system-gray-400">
              Apply to:
              {(candidates ?? []).slice(0, 8).map(candidate => (
                <button
                  key={candidate.id}
                  type="button"
                  onClick={() => onSelectAsset?.(candidate)}
                  className="w-8 h-8 rounded overflow-hidden ring-1 ring-system-gray-700/60 hover:ring-brand"
                  title="Apply the current layout to this media"
                >
                  <img
                    src={candidate.thumbnailUrl || candidate.url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </span>
          )
        )}
      </div>

      {/* Canvas aspect ratio */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] uppercase tracking-wide text-system-gray-500">
          Canvas
        </span>
        {RATIOS.map(ratio => (
          <button
            key={ratio.key}
            type="button"
            onClick={() => changeRatio(ratio.key)}
            disabled={
              busy ||
              !canvasDims ||
              (ratio.key === "original" && !naturalDimsRef.current)
            }
            className={cn(
              "btn btn-xs border-system-gray-700",
              ratioKey === ratio.key
                ? "bg-brand/20 border-brand/60 text-white"
                : "bg-system-gray-800 text-system-gray-300 hover:text-white",
              ratio.key === "original" &&
                !naturalDimsRef.current &&
                "opacity-40",
            )}
          >
            {ratio.label}
          </button>
        ))}
        {canvasDims && (
          <span className="text-[10px] text-system-gray-500">
            {canvasDims.width}×{canvasDims.height}
          </span>
        )}
      </div>

      {/* AI instruction row */}
      <div className="flex flex-col gap-2">
        {/* Iteration thread — what you asked, what the AI did, turn by turn. */}
        {conversation.length > 0 && (
          <div className="max-h-32 overflow-y-auto flex flex-col gap-1 rounded-lg border border-system-gray-800 bg-system-gray-900/40 px-2.5 py-2">
            {conversation.map((turn, i) => (
              <p
                key={`${i}-${turn.role}`}
                className={cn(
                  "text-[11px] leading-snug",
                  turn.role === "user"
                    ? "text-system-gray-300"
                    : "text-brand/80 italic",
                )}
              >
                <span className="font-semibold not-italic">
                  {turn.role === "user" ? "You: " : "AI: "}
                </span>
                {turn.text}
              </p>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1.5 flex-wrap">
          {PRESETS.map(p => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPreset(prev => (prev === p.key ? null : p.key))}
              className={cn(
                "btn btn-xs border-system-gray-700",
                preset === p.key
                  ? "bg-brand/20 border-brand/60 text-white"
                  : "bg-system-gray-800 text-system-gray-300 hover:text-white",
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        {selectionLabel && (
          <div className="flex items-center gap-1.5 text-[11px]">
            <span
              className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5"
              style={{ borderColor: "#EB89B566", color: "#EB89B5" }}
            >
              AI will focus the selected element: {selectionLabel}
              <button
                type="button"
                onClick={() => setSelectedLayerId(null)}
                title="Clear target — the instruction applies to the whole canvas"
                aria-label="Clear AI target"
                className="hover:opacity-70"
              >
                ×
              </button>
            </span>
          </div>
        )}
        <div className="flex gap-2 items-start">
          <textarea
            value={instruction}
            onChange={e => setInstruction(e.target.value)}
            rows={2}
            placeholder={
              doc
                ? 'Ask the AI to change this canvas — e.g. "make the headline bigger and move the logo to the top left"'
                : 'Describe the layout — e.g. "lab-tested tag above a huge boxed MOLD FREE headline, screening note + logo at the bottom"'
            }
            className="textarea textarea-sm flex-1 bg-system-gray-900/60 border-system-gray-700 text-white"
            disabled={busy}
          />
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              onClick={() => void runAi()}
              disabled={busy || !canvasDims}
              className="btn btn-sm bg-brand/20 border-brand/60 text-white hover:bg-brand/30 gap-1.5 whitespace-nowrap"
            >
              <SparklesIcon className="w-4 h-4" />
              {aiBusy
                ? "Designing…"
                : doc
                  ? "Ask AI to edit"
                  : "Design with AI"}
            </button>
            {!doc && (
              <button
                type="button"
                onClick={startBlank}
                disabled={busy || !canvasDims}
                className="btn btn-xs btn-ghost text-system-gray-400"
              >
                Start blank
              </button>
            )}
            {undoStack.length > 0 && (
              <button
                type="button"
                onClick={undoAiTurn}
                disabled={busy}
                className="btn btn-xs btn-ghost text-system-gray-400"
                title="Restore the canvas as it was before the last AI change"
              >
                Undo AI
              </button>
            )}
            {speech.supported && (
              <button
                type="button"
                onClick={speech.toggle}
                disabled={busy}
                className="btn btn-xs btn-ghost gap-1"
                style={{ color: speech.listening ? "#EF4444" : "#9CA3AF" }}
                title={
                  speech.listening
                    ? "Stop dictation"
                    : "Dictate your instruction (English)"
                }
              >
                <MicrophoneIcon className="w-3.5 h-3.5" />
                {speech.listening ? "Stop" : "Dictate"}
              </button>
            )}
          </div>
        </div>
        {speech.listening && (
          <p className="text-[11px] italic" style={{ color: "#EF4444" }}>
            ● Listening… {speech.interimText}
          </p>
        )}
      </div>

      {/* Canvas */}
      {!canvasDims ? (
        <div className="py-10 text-center text-system-gray-500 text-sm">
          Loading media…
        </div>
      ) : doc ? (
        <>
          <OverlayCanvasEditor
            doc={doc}
            onChange={setDoc}
            media={
              asset
                ? { type: isVideo ? "video" : "image", src: asset.url }
                : null
            }
            logoUrl={logoUrl}
            brandKit={brandKit}
            selectedId={selectedLayerId}
            onSelectId={setSelectedLayerId}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <input
              value={layoutName}
              onChange={e => setLayoutName(e.target.value)}
              placeholder="Layout name"
              className="input input-sm w-48 bg-system-gray-900/60 border-system-gray-700 text-white"
              disabled={busy}
            />
            <button
              type="button"
              onClick={() => void handleSaveLayout()}
              disabled={busy}
              className="btn btn-sm bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white"
            >
              {isSavingLayout
                ? "Saving…"
                : activeLayoutId
                  ? "Update layout"
                  : "Save as layout"}
            </button>

            <span className="mr-auto" />
            {exporting && (
              <span className="text-xs text-system-gray-400">
                {isVideo
                  ? `Rendering video… ${exporting.progress !== null ? `${Math.round(exporting.progress * 100)}%` : ""}`
                  : "Rendering…"}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="btn btn-sm btn-ghost text-system-gray-400"
            >
              Done
            </button>
            <button
              type="button"
              onClick={() => void handleExport("download")}
              disabled={busy || !asset}
              title={asset ? undefined : "Pick media (Apply to) first"}
              className="btn btn-sm bg-system-gray-800 border-system-gray-700 text-system-gray-300 hover:text-white gap-1.5"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Download
            </button>
            <button
              type="button"
              onClick={() => void handleExport("save")}
              disabled={busy || !asset}
              title={asset ? undefined : "Pick media (Apply to) first"}
              className="btn btn-sm bg-system-green-600 border-system-green-600 text-white hover:bg-system-green-500"
            >
              Save to assets
            </button>
          </div>
        </>
      ) : (
        <div className="py-8 text-center text-system-gray-500 text-sm border border-dashed border-system-gray-700/50 rounded-lg">
          {(layouts?.length ?? 0) > 0
            ? "Pick a saved layout above, or describe a new one and let the AI design the first pass."
            : "Describe a layout above and let the AI design the first pass — or start blank and build it yourself."}{" "}
          Layouts are reusable: design once, then apply to any image or video
          while building an ad.
        </div>
      )}

      <BrandKitModal
        open={brandKitOpen}
        onClose={() => setBrandKitOpen(false)}
      />
    </div>
  )
}
